<template>
    <popup-modal
        position="center"
        :closeOnClick="!working"
        :has-close-button="hasCloseButton"
        :without-padding="true"
        :custom-classes="customClasses"
        :use-portal="usePortal"
        @close="close"
    >
        <template v-if="fetchingData">
            <div :class="withoutPadding ? '' : deleteAction ? 'p-28 pb-32' : 'p-48'">
                <loading/>
            </div>
        </template>
        <template v-else>
            <div :class="withoutPadding ? '' : deleteAction ? 'p-28 pb-32' : 'p-24'"
                 class="sm:flex sm:items-start"
            >
                <div v-if="deleteAction || showExclamation"
                     class="mx-auto shrink-0 flex items-center justify-center h-48 w-48 rounded-32 bg-semantic-error-light sm:mx-0 sm:h-40 sm:w-40 sm:mr-18"
                >
                    <svg-icon type="warning" class="w-20 h-20 fill-semantic-error -mt-2" />
                </div>

                <div class="mt-6 text-center sm:mt-0 sm:text-left">
                    <h2 class="first-letter:uppercase block text-heading-2 mb-8">
                        <template v-if="!title && deleteAction">
                            {{ $t('general.delete') }}?
                        </template>
                        <template v-else>
                            {{ title }}
                        </template>
                    </h2>

                    <div :class="showBig ? 'max-w-640' : 'max-w-480'"
                         class="message text-body-sm text-black my-16"
                         v-html="message"></div>
                    <slot/>

                    <checkbox v-if="toggleText" v-model="canConfirm" class="flex justify-left mt-14">
                        {{ toggleText }}
                    </checkbox>
                </div>
            </div>

            <div class="bg-primary-5 py-16 px-24 space-y-16 sm:space-y-0 sm:flex sm:flex-row-reverse rounded-b">
                <simple-button
                    v-if="showConfirmButton"
                    :display="showExclamation ? 'danger' : 'secondary'"
                    look="primary"
                    :disabled="!canConfirm || disabled || working"
                    :loading="working"
                    :icon="confirmIcon && !working ? confirmIcon : null"
                    @click="confirm"
                    class="sm:ml-8"
                >
                    {{ confirmationText }}
                </simple-button>
                <simple-button
                    v-if="showCancelButton"
                    :disabled="working"
                    @click="cancel"
                >
                    {{ cancellationText }}
                </simple-button>
            </div>
        </template>
    </popup-modal>
</template>

<script>
import PopupModal from './PopupModal.vue';
import SimpleButton from './SimpleButton.vue';
import _upperFirst from 'lodash/upperFirst';
import Loading from './Loading';
import Checkbox from '../forms/Checkbox';
import SvgIcon from './SvgIcon';

export default {
    props: {
        title: String,
        message: String,
        action: Function,
        confirmButton: {type: String, default: ''},
        cancelButton: {type: String, default: ''},
        confirmIcon:  {type: String, default: ''},
        customClasses: { type: String, default: '' },
        confirmText: String,
        cancelText: String,
        showConfirmButton: {type: Boolean, default: true},
        showCancelButton: {type: Boolean, default: true},
        working: Boolean,
        fetchingData: Boolean,
        disabled: Boolean,
        showBig: Boolean,
        showExclamation: Boolean,
        toggleText: String,
        hasCloseButton: {type: Boolean, default: false},
        withoutPadding: {
            type: Boolean,
            default: false,
        },
        swapButtonLogic: {type: Boolean, default: false}, //Use this to swap button colors but keep the functionality
        usePortal: {type: Boolean, default: true},
    },
    components: {
        SvgIcon,
        Checkbox,
        'popup-modal': PopupModal,
        'simple-button': SimpleButton,
        'loading': Loading,
    },
    data() {
        return {
            canConfirm: !this.toggleText,
        };
    },
    computed: {
        deleteAction() {
            if (this.showExclamation === false)
                return false;

            return !(this.swapButtonLogic || this.cancelButton === 'success' || this.confirmButton === 'success');
        },
        cancellationButton() {
            if (this.swapButtonLogic) {
                this.cancelButton = 'success';
            }

            return (this.cancelButton === 'success' ? 'bg-secondary-80 hover:bg-secondary text-white' : this.cancelButton) || 'bg-white hover:bg-primary-5 shadow';
        },
        confirmationButton() {
            if (this.swapButtonLogic) {
                this.confirmButton = 'bg-white hover:bg-primary-5 text-primary-80 shadow';
            }

            if (!this.canConfirm || this.disabled) {
                return 'bg-primary-5 text-primary-80';
            }

            return (this.confirmButton === 'success' ? 'bg-secondary-80 hover:bg-secondary' : this.confirmButton) || 'bg-semantic-error hover:bg-semantic-error';
        },
        confirmationText() {
            return _upperFirst(this.confirmText || this.$t('general.confirm'));
        },
        cancellationText() {
            return _upperFirst(this.cancelText || this.$t('general.cancel'));
        },
    },
    methods: {
        close() {
            this.$emit('close');
        },
        cancel() {
            this.close();
        },
        confirm() {
            this.$emit('confirm');
        },
    },
};
</script>
<style>
    .message a {
        color: #3AB9E5;
        text-decoration: underline;
    }
</style>
